import { API_BASE } from '../lib/apiOrigin';
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Star, ExternalLink, ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const API = API_BASE;

// Official multi-color "G" Google mark — customers recognise this on real
// reviews. A generic star or globe would look untrustworthy here.
const GoogleG = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const StarRow = ({ rating }) => (
  <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={14}
        className={i <= rating ? "text-amber-400 fill-amber-400" : "text-zinc-600"}
      />
    ))}
  </div>
);

const AUTO_SCROLL_MS = 5000;

/**
 * Public homepage carousel of admin-curated Google Reviews with a Verified
 * badge, auto-scroll, manual prev/next controls, and pause-on-hover.
 * Keep the navigation destination useful while loading, empty or unavailable.
 */
const GoogleReviews = () => {
  const { t, lang } = useLang();
  const [reviews, setReviews] = useState([]);
  const [loadStatus, setLoadStatus] = useState("loading");
  const [businessUrl, setBusinessUrl] = useState("");
  const [index, setIndex] = useState(0);
  const [pageSize, setPageSize] = useState(3);
  const [paused, setPaused] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/google-reviews`).catch(() => ({ data: [], failed: true })),
      axios.get(`${API}/settings/business-profile`).catch(() => ({ data: {} })),
    ]).then(([r, b]) => {
      if (cancelled) return;
      setReviews(Array.isArray(r.data) ? r.data : []);
      setLoadStatus(r.failed || !Array.isArray(r.data) ? "error" : "ready");
      setBusinessUrl(b.data?.google_business_url || "");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Responsive page size for the carousel. 1 on mobile, 2 on tablet, 3 on desktop.
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w >= 1024) setPageSize(2);
      else if (w >= 640) setPageSize(2);
      else setPageSize(1);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  // Auto-scroll (pauses on hover). Advances one card at a time so every
  // review is eventually visible.
  useEffect(() => {
    if (paused || reviews.length <= pageSize) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % reviews.length);
    }, AUTO_SCROLL_MS);
    return () => clearInterval(id);
  }, [paused, reviews.length, pageSize]);

  // Reset index if reviews shrink below current position (edge case).
  useEffect(() => {
    if (index >= reviews.length && reviews.length > 0) setIndex(0);
  }, [index, reviews.length]);

  const visible = useMemo(() => {
    if (!reviews.length) return [];
    if (reviews.length <= pageSize) return reviews;
    // Wrap around so the carousel is truly circular.
    return Array.from({ length: pageSize }, (_, i) => reviews[(index + i) % reviews.length]);
  }, [reviews, index, pageSize]);

  const heading = t?.googleReviews?.heading || "Real Customers. Real Projects. Real Reviews.";
  const eyebrow = t?.googleReviews?.eyebrow || "Google Reviews";
  const readMore = t?.googleReviews?.read_more || "Read More Reviews on Google";
  const viewLabel = t?.googleReviews?.view_on_google || "View on Google";
  const verifiedLabel = t?.googleReviews?.verified || "Verified";
  const copy = t?.googleReviews || {};

  if (!reviews.length) return (
    <section
      id="reviews"
      aria-labelledby="reviews-heading"
      data-testid="reviews-empty"
      className="relative py-16 sm:py-24 bg-zinc-950 border-t border-white/5"
    >
      <div className="mx-auto max-w-3xl px-5 sm:px-6 text-center">
        <h2 id="reviews-heading" className="font-display font-black text-white text-3xl sm:text-5xl tracking-tight">
          {copy.empty_heading || "Customer reviews"}
        </h2>
        <p role="status" className="mt-6 text-zinc-300 text-base sm:text-lg">
          {loadStatus === "loading"
            ? copy.loading || "Loading customer reviews…"
            : loadStatus === "error"
              ? copy.unavailable || "Customer reviews are temporarily unavailable."
              : copy.empty_status || "No customer reviews are published here yet."}
        </p>
        <p className="mt-3 text-zinc-400 leading-relaxed">
          {copy.empty_help || "For customer references, use the contact details in the footer."}
        </p>
        {businessUrl && (
          <div className="mt-8 flex justify-center">
            <a href={businessUrl} target="_blank" rel="noreferrer" data-testid="google-reviews-read-more" className="btn-primary inline-flex items-center gap-3">
              <GoogleG size={18} />
              {readMore}
              <ExternalLink size={16} />
            </a>
          </div>
        )}
      </div>
    </section>
  );

  const canPaginate = reviews.length > pageSize;
  const prev = () => setIndex((i) => (i - 1 + reviews.length) % reviews.length);
  const next = () => setIndex((i) => (i + 1) % reviews.length);

  return (
    <section
      id="reviews"
      aria-labelledby="reviews-heading"
      data-testid="google-reviews-section"
      className="relative py-24 sm:py-32 bg-zinc-950 border-t border-white/5"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14 sm:mb-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2.5 text-[10px] uppercase tracking-[0.3em] font-semibold text-sky-400 mb-5">
              <GoogleG size={16} />
              {eyebrow}
            </div>
            <h2 id="reviews-heading" className="font-display font-black text-white text-3xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {heading}
            </h2>
          </div>

          {canPaginate && (
            <div className="flex items-center gap-2">
              <button
                data-testid="google-reviews-prev"
                onClick={prev}
                aria-label={copy.previous || "Previous review"}
                className="h-11 w-11 border border-white/15 text-white hover:border-sky-400 hover:text-sky-300 flex items-center justify-center transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                data-testid="google-reviews-next"
                onClick={next}
                aria-label={copy.next || "Next review"}
                className="h-11 w-11 border border-white/15 text-white hover:border-sky-400 hover:text-sky-300 flex items-center justify-center transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>

        <div
          ref={wrapRef}
          className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6"
        >
          {visible.map((r, i) => (
            <article
              key={`${r.id}-${i}`}
              data-testid={`google-review-${r.id}`}
              className="bg-zinc-900 border border-white/10 p-6 sm:p-7 flex flex-col hover:border-sky-400/40 transition-all duration-500"
              style={{ animation: "fade-in 500ms ease-out" }}
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  {r.profile_photo ? (
                    <img src={r.profile_photo} alt="" className="h-11 w-11 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="h-11 w-11 rounded-full bg-sky-500/20 border border-sky-400/40 flex items-center justify-center text-sky-300 font-display font-black text-lg shrink-0">
                      {(r.customer_name || "?")[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="text-white font-semibold text-base leading-tight truncate">
                        {r.customer_name}
                      </div>
                      {r.verified && (
                        <span
                          data-testid={`google-review-verified-${r.id}`}
                          className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] font-bold text-emerald-400 border border-emerald-400/40 bg-emerald-500/10 px-1.5 py-0.5"
                          title={verifiedLabel}
                        >
                          <ShieldCheck size={10} />
                          {verifiedLabel}
                        </span>
                      )}
                    </div>
                    <StarRow rating={r.rating} />
                  </div>
                </div>
                <GoogleG size={22} />
              </div>
              <p className="text-zinc-200 text-sm sm:text-base leading-relaxed whitespace-pre-line flex-1">
                {r.text}
              </p>
              {r.is_excerpt && <p className="mt-3 text-xs text-zinc-400">{lang === 'pt' ? 'Trecho da avaliação publicada no Google' : lang === 'es' ? 'Extracto de la reseña publicada en Google' : 'Excerpt of a review posted on Google'}</p>}
              <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between gap-3 text-xs">
                <span className="text-zinc-400">{r.review_date}</span>
                {r.google_review_url && (
                  <a
                    href={r.google_review_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`google-review-view-${r.id}`}
                    className="inline-flex items-center gap-1.5 text-sky-400 hover:text-sky-300 font-semibold uppercase tracking-[0.15em]"
                  >
                    {viewLabel}
                    <ExternalLink size={13} />
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>

        {/* Progress dots */}
        {canPaginate && (
          <div className="mt-10 flex items-center justify-center gap-2" data-testid="google-reviews-dots">
            {reviews.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`${copy.go_to_review || "Go to review"} ${i + 1}`}
                aria-current={i === index ? "true" : undefined}
                className={`h-2 transition-all ${
                  i === index ? "w-8 bg-sky-400" : "w-2 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>
        )}

        {businessUrl && (
          <div className="mt-12 flex justify-center">
            <a
              href={businessUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="google-reviews-read-more"
              className="btn-primary inline-flex items-center gap-3"
            >
              <GoogleG size={18} />
              {readMore}
              <ExternalLink size={16} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default GoogleReviews;
